import type { PivotLayout, PivotLayoutFilter } from '@ztechapps/common.contract'
import type { PivotData } from './types'
import { isInPivotDateRange } from '@ztechapps/common.utils'
import { PIVOT_FILTER_ALL_TEXT, PIVOT_FILTER_SELECTED_SUFFIX } from './constants'
import { createPivotMemberMatcher } from './createPivotMemberMatcher'
import { listPivotMembers } from './listPivotMembers'
import { findPivotLayoutItem } from './pivotLayout.utils'
import { toPivotFieldLabel } from './toPivotFieldLabel'

/** A filter-only field's label and a summary of the members it keeps. */
export type PivotFilterChip = { filter: PivotLayoutFilter; label: string; text: string }

/** Lists the filters that no Rows or Columns item or measure shows, each with its label and kept-members text. */
export const listPivotFilterChips = ({
  data,
  layout,
  timeZone,
}: {
  data: PivotData
  layout: Pick<PivotLayout, 'rows' | 'columns' | 'measures' | 'filters'>
  timeZone: string
}): PivotFilterChip[] =>
  layout.filters.flatMap((filter) => {
    const pivotField = data.fields.find((item) => item.name === filter.field)
    const { dateRange } = filter
    const isShown =
      findPivotLayoutItem(layout.rows, filter) ||
      findPivotLayoutItem(layout.columns, filter) ||
      layout.measures.some((measure) => measure.field === filter.field)
    // a ranged filter stays visible even on a shown field, so the export header states the range too
    if (!pivotField || (isShown && dateRange === undefined)) return []
    const members = listPivotMembers({ data, field: filter, timeZone })
    const matches = createPivotMemberMatcher({
      includedValues: filter.includedValues,
      excludedValues: filter.excludedValues,
    })
    // out-of-range days fall out of the range itself, so only in-range days shape the member text
    const inRange =
      dateRange === undefined ? members : members.filter((member) => isInPivotDateRange(member.key, dateRange))
    const kept = inRange.filter((member) => matches(member.key))
    const memberText =
      kept.length === inRange.length
        ? PIVOT_FILTER_ALL_TEXT
        : kept.length === 1
          ? kept[0].label
          : `${kept.length} ${PIVOT_FILTER_SELECTED_SUFFIX}`
    const text =
      dateRange === undefined
        ? memberText
        : `${dateRange.from} to ${dateRange.to}${memberText === PIVOT_FILTER_ALL_TEXT ? '' : `, ${memberText}`}`
    return [{ filter, label: toPivotFieldLabel(pivotField, filter.field, filter.dateGroup), text }]
  })
